import { useState, useRef, useCallback } from 'react';
import { getSupabase } from '../components/supabaseClient';
import { bucketName_files, tableName, prompts, colid, generateDocFilename } from '../components/env';
import {
  checkFrontendStorageJsonBlobSize,
  isProbablyJsonStoragePath,
  preflightRestrictedStorageJsonSize,
} from '../utils/storageJsonFrontendLimit';
import { templates as documentTemplates } from '../assets/templates/index';
import { calculateAge } from '../utils/ageCalculation';
import { updateLastUpdated } from '../documentComponents/documentUtilities';
import { useToast } from '@chakra-ui/react';

/**
 * Custom hook for document operations (loading, saving, scanning, etc.)
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.editorRef - Reference to the editor component
 * @param {string} params.filePath - Path to the document file
 * @param {Array} params.sections - Current sections state
 * @param {Function} params.setSections - Setter for sections state
 * @param {Function} params.setActiveSection - Setter for active section
 * @param {string} params.extractedName - Patient name
 * @param {string} params.validId - Patient ID
 * @param {string} params.effectiveDate - Document date
 * @param {string} params.correctedType - Patient type (NP, FFP, FFS, etc.)
 * @param {boolean} params.isCombined - Whether this is a combined document
 * @param {boolean} params.createNew - Whether to create a new document
 * @param {string} params.patientScheduledTime - Patient's scheduled time
 * @param {string} params.navbarTime - Time to display in navbar
 * @param {string} params.name - Document name
 * @param {Object} params.userInfo - User information object
 * @param {Object} params.clientId - Client ID ref
 * @param {Function} params.setDocumentLoaded - Setter for document loaded state
 * @param {Function} params.setContentLoadingComplete - Setter for content loading complete state
 * @param {Function} params.setTime - Setter for time state
 * @param {Function} params.setIsSaving - Setter for isSaving state
 * @param {Function} params.setSaveSuccess - Setter for saveSuccess state
 * @param {Function} params.setIsUnmounting - Setter for isUnmounting state
 * @param {Function} params.checkContentStability - Function to check content stability
 * @param {Object} params.delayedScanExecuted - Ref for delayed scan execution
 * @param {Object} params.contentLoadingLock - Ref for content loading lock
 * @param {Object} params.contentLoadAttempts - Ref for content load attempts
 * @param {number} params.maxContentLoadAttempts - Maximum content load attempts
 * @param {Object} params.toast - Toast function from Chakra UI
 * 
 * @returns {Object} Document operations functions and state
 */
export function useDocumentOperations({
  editorRef,
  filePath,
  sections,
  setSections,
  setActiveSection,
  extractedName,
  validId,
  effectiveDate,
  correctedType,
  isCombined,
  createNew,
  patientScheduledTime,
  navbarTime,
  name,
  userInfo,
  clientId,
  setDocumentLoaded,
  setContentLoadingComplete,
  setTime,
  setIsSaving,
  setSaveSuccess,
  isUnmounting,
  setIsUnmounting,
  checkContentStability,
  delayedScanExecuted,
  contentLoadingLock,
  contentLoadAttempts,
  maxContentLoadAttempts,
  toast,
  onDocumentRowUpsert,
}) {
  const toastHook = useToast();

  /** Get current user information */
  const getCurrentUser = useCallback(async () => {
    try {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      if (user) {
        // Get user name from database
        const { data: userData } = await supabase
          .from('users')
          .select('name')
          .eq('id', user.id)
          .single();
        
        const userName = userData?.name || user.email || 'Anonymous';
        return { id: user.id, name: userName };
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
    return { id: null, name: 'Anonymous' };
  }, []);

  /** Manually scan for sections in the editor */
  const scanForSections = useCallback(() => {
    if (!editorRef.current) return;

    if (isCombined) {
      console.log('🔍 Scanning for sections in combined document...');
    }
    const newSections = [];

    for (let pageIndex = 0; pageIndex < editorRef.current.getEditorCount(); pageIndex++) {
      const editor = editorRef.current.getEditor(pageIndex);
      const lines = editor.getLines();
      
      if (isCombined) {
        console.log(`🔍 Processing page ${pageIndex} with ${lines.length} lines`);
      }
      
      lines.forEach((line) => {
        const text = line.domNode?.innerText.trim();
        const format = editor.getFormat(editor.getIndex(line));

        // Check for header format or patient section pattern (time + name + ID + type)
        const isHeader = format.header || 
          (text && /^\d{1,2}:\d{2}\s+[A-Za-z\s,]+-\s*\w+\s+[A-Z]{2,4}$/.test(text));
        
        if (isHeader && text && text.trim() !== '') {
          const sectionId = `section-${newSections.length}`;
          newSections.push({
            id: sectionId,
            title: text,
            level: format.header || 1,
            isEditing: false,
            createdAt: new Date().toISOString(),
            lastupdated: new Date().toISOString(),
          });
        }
      });
    }

    // Update active section based on cursor position
    if (editorRef.current) {
      const editor = editorRef.current.getEditor(0);
      const position = editor.getSelection()?.index || 0;
      newSections.forEach((section) => {
        if (editorRef.current && editorRef.current.getBoundsForSection) {
          const [startIndex, endIndex] = editorRef.current.getBoundsForSection(0, section.title);
          if (position > startIndex && position <= endIndex) {
            section.isEditing = true;
            setActiveSection(section.id);
          } else {
            section.isEditing = false;
          }
        }
      });
    }

    // If no sections were found, create a default section
    if (newSections.length === 0) {
      console.log('🔍 No sections found, creating fallback section');
      newSections.push({
        id: 'section-0',
        title: isCombined ? 'Combined Document' : 'Document',
        level: 1,
        isEditing: false,
        createdAt: new Date().toISOString(),
        lastupdated: new Date().toISOString(),
      });
    }
    
    setSections(newSections);
    
    // For FFP/FFS documents, also scan after a longer delay
    if ((correctedType === 'FFP' || correctedType === 'FFS') && !delayedScanExecuted.current) {
      delayedScanExecuted.current = true;
      checkContentStability(() => {
        console.log('🔍 Running delayed scan for FFP/FFS document...');
        scanForSections();
      }, 2000);
    }
    
    // For combined documents, also scan after a longer delay
    if (isCombined && !delayedScanExecuted.current) {
      delayedScanExecuted.current = true;
      checkContentStability(() => {
        console.log('🔍 Running delayed scan for combined document...');
        scanForSections();
      }, 1500);
    }
  }, [
    editorRef,
    isCombined,
    correctedType,
    setSections,
    setActiveSection,
    delayedScanExecuted,
    checkContentStability,
  ]);

  /** Update the "documents" table in Supabase with the full content */
  const updateDocumentTable = useCallback(async (editor, currentDelta) => {
    const supabase = await getSupabase();
    console.log(`💾 [DB Update] Upserting documents table with path: "${filePath}"`);
    try {
      const { data, error } = await supabase
        .from("documents")
        .upsert(
          [
            {
              content: currentDelta,
              path: filePath,
              latest_update: new Date().toISOString(),
              client_id: clientId.current,
            },
          ],
          { onConflict: "path" }
        )
        .select("id, path, latest_update")
        .single();

      if (error) {
        console.error(`💾 [DB Update] Error updating table: ${error.message}`, error);
      } else {
        console.log(`💾 [DB Update] Success — row id: ${data?.id}, path: ${data?.path}, updated: ${data?.latest_update}`);
        if (data?.id && typeof onDocumentRowUpsert === "function") {
          onDocumentRowUpsert(data.id);
        }
      }
    } catch (err) {
      console.error(`💾 [DB Update] Exception: ${err}`);
    }
  }, [filePath, clientId, onDocumentRowUpsert]);

  /** Save the full document content to Supabase storage */
  const handleSaveDoc = useCallback(async () => {
    const supabase = await getSupabase();
    const editor = editorRef.current?.getEditor(0);
    if (!editor) {
      console.log('Editor not available for saving');
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);

    const currentDelta = editor.getContents();

    const dataToSave = {
      sections,
      content: currentDelta,
      delta: currentDelta,
      lastUpdated: new Date().toISOString(),
    };

    try {
      const file = new Blob([JSON.stringify(dataToSave, null, 2)], {
        type: "application/json",
      });

      const { error } = await supabase.storage
        .from(bucketName_files)
        .upload(filePath, file, {
          upsert: true,
          contentType: "application/json",
        });

      if (error) {
        console.error("Error uploading file:", error.message);
        setSaveSuccess(false);
      } else {
        console.log(`File successfully saved at: ${filePath}`);
        setSaveSuccess(true);
      }

      if (!isUnmounting) {
        await updateDocumentTable(editor, currentDelta);
      }
    } catch (err) {
      console.error("Error saving document:", err.message);
      setSaveSuccess(false);
    } finally {
      if (!isUnmounting) {
        setIsSaving(false);
      }
    }
  }, [
    editorRef,
    filePath,
    sections,
    isUnmounting,
    setIsSaving,
    setSaveSuccess,
    updateDocumentTable,
  ]);

  /** Remove duplicate content from the document */
  const handleRemoveDuplicates = useCallback(async () => {
    const editor = editorRef.current?.getEditor(0);
    if (!editor) {
      console.log('Editor not available for duplicate removal');
      return;
    }

    try {
      const currentDelta = editor.getContents();
      console.log('🔍 Starting simple duplicate removal...');

      if (!currentDelta || !currentDelta.ops) {
        console.log('No content to process');
        return;
      }

      const fullText = editor.getText();
      const lines = fullText.split('\n');
      
      // Find first duplicate line
      let firstDuplicateIndex = -1;
      let firstLine = '';
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.length > 0) {
          firstLine = line;
          break;
        }
      }
      
      if (firstLine.length === 0) {
        console.log('No content found in document');
        return;
      }
      
      // Look for the first line to repeat
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.length > 0 && line === firstLine) {
          firstDuplicateIndex = i;
          break;
        }
      }

      if (firstDuplicateIndex === -1) {
        (toast || toastHook)({
          title: "No Duplicates Found",
          description: "No duplicate content was found in this document.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Get the text content that should be kept
      const cleanLines = lines.slice(0, firstDuplicateIndex);
      const cleanText = cleanLines.join('\n');
      
      // Find the operation that contains the end of our clean text
      let cutIndex = -1;
      let accumulatedText = '';
      
      for (let i = 0; i < currentDelta.ops.length; i++) {
        const op = currentDelta.ops[i];
        if (op.insert && typeof op.insert === 'string') {
          accumulatedText += op.insert;
          if (accumulatedText.includes(cleanText) && accumulatedText.length >= cleanText.length) {
            cutIndex = i + 1;
            break;
          }
        }
      }

      if (cutIndex === -1) {
        console.log('Could not find cut point in delta operations');
        return;
      }

      // Keep only operations up to the cut point
      const cleanOps = currentDelta.ops.slice(0, cutIndex);
      const cleanDelta = {
        ...currentDelta,
        ops: cleanOps
      };

      editor.setContents(cleanDelta, 'api');

      const removedOps = currentDelta.ops.length - cleanOps.length;
      (toast || toastHook)({
        title: "Duplicates Removed",
        description: `Successfully removed ${removedOps} duplicate operations.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Auto-save the cleaned document
      setTimeout(() => {
        handleSaveDoc();
      }, 1000);

    } catch (error) {
      console.error('Error removing duplicates:', error);
      (toast || toastHook)({
        title: "Error",
        description: "Failed to remove duplicates. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [editorRef, toast, toastHook, handleSaveDoc]);

  /** Fetch and apply updated document content */
  const fetchUpdatedDocument = useCallback(async (filePath) => {
    const supabase = await getSupabase();
    try {
      let preflightTrust = false;
      if (isProbablyJsonStoragePath(filePath)) {
        const pf = await preflightRestrictedStorageJsonSize(
          supabase,
          bucketName_files,
          filePath,
        );
        if (pf.status === "too_large") {
          (toast || toastHook)({
            title: "Document too large to refresh",
            description: pf.message,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
          return;
        }
        if (pf.status === "not_found") {
          console.warn("fetchUpdatedDocument: file not found (preflight)", filePath);
          return;
        }
        if (pf.status === "ok") {
          preflightTrust = true;
        }
      }

      const { data, error } = await supabase.storage
        .from(bucketName_files)
        .download(filePath);

      if (error) {
        console.error("Error fetching file:", error.message);
        return;
      }

      if (
        !preflightTrust &&
        isProbablyJsonStoragePath(filePath)
      ) {
        const sc = checkFrontendStorageJsonBlobSize(data);
        if (!sc.ok) {
          (toast || toastHook)({
            title: "Document too large to refresh",
            description: sc.message,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
          return;
        }
      }

      const fileContent = await data.text();
      const parsedData = JSON.parse(fileContent);
      const editor = editorRef.current?.getEditor(0);
      if (editor && parsedData.delta) {
        editor.setContents(parsedData.delta || []);
      }
      console.log("Remote update applied successfully.");
    } catch (err) {
      console.error("Error fetching document:", err.message);
    }
  }, [editorRef, toast, toastHook]);

  return {
    scanForSections,
    handleSaveDoc,
    handleRemoveDuplicates,
    updateDocumentTable,
    getCurrentUser,
    fetchUpdatedDocument,
  };
}

